import { useCallback, useEffect, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import WOService from "src/Services/API/wo.service";
import MaterialService from "src/Services/API/material.service";
import POService from "src/Services/API/po.service";
import UserService from "src/Services/API/user.service";
import Loader from "src/Components/Common/Loader";
import WoMetaRow from "src/Components/Common/wometarow";
import RichTextEditor from "src/Components/Common/richtexteditor";
import PreviewSTModal from "src/Pages/WO/Components/previewstmodal";
import TimeInput, { parseUserTime } from "src/Components/Common/timeinput";
import ConfirmDialog from "src/Common/confirmdialog";
import { getAvatarUrl, getServiceTicketUrl, formatDate } from "src/Common/common";
import { can, handleError, getFormattedHoursFromMins, getMinutesFromFormattedHours } from "src/Common/function";
import { vocabularize } from "src/Common/vocab";

// Hours are stored as "HH:MM" strings; an unset cell prints as a dash.
const NO_HOURS = "—";
const isBlankHours = (value) => !value || value === "00:00" || value === NO_HOURS || value === "__:__";
const minutesOf = (value) => (isBlankHours(value) ? 0 : getMinutesFromFormattedHours(value));

// A category counts as a refrigerant panel by name, the same rule the refrigerant
// report and the printed ticket use. "customer" in the name means the customer
// supplied it; any other refrigerant category is company supplied.
const isRefrigerantCategory = (name) => (name || "").toLowerCase().includes("refrigerant");
const isCustomerSupplied = (name) => (name || "").toLowerCase().includes("customer");

/* -------------------------------------------------------------- primitives */

function Card({ title, action, children, className = "" }) {
  return (
    <div
      className={`w-full bg-white rounded-xl border border-[#E5E8F3] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] overflow-hidden ${className}`}
    >
      {title && (
        <div className="self-stretch p-4 bg-[#F8F9FE] border-b border-[#E5E8F3] flex items-center justify-between gap-3">
          <span className="text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6 capitalize">{title}</span>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function AddRowButton({ label = "Add Row", onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-1 rounded-md flex items-center gap-2 text-[#0065FD] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize cursor-pointer"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 11 11" fill="none">
        <path
          d="M9.83333 4.66667C10.1095 4.66667 10.3333 4.89052 10.3333 5.16667C10.3333 5.44281 10.1095 5.66667 9.83333 5.66667H0.5C0.223858 5.66667 0 5.44281 0 5.16667C0 4.89052 0.223858 4.66667 0.5 4.66667H9.83333Z"
          fill="#0065FD"
        />
        <path
          d="M4.66667 9.83333V0.5C4.66667 0.223858 4.89052 0 5.16667 0C5.44281 0 5.66667 0.223858 5.66667 0.5V9.83333C5.66667 10.1095 5.44281 10.3333 5.16667 10.3333C4.89052 10.3333 4.66667 10.1095 4.66667 9.83333Z"
          fill="#0065FD"
        />
      </svg>
      <span>{label}</span>
    </button>
  );
}

function EditRowButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="w-[20px] h-[20px] flex items-center justify-center text-[#7C829A] hover:text-[#0065FD] cursor-pointer"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
      </svg>
    </button>
  );
}

function DeleteRowButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="w-[20px] h-[20px] flex items-center justify-center text-[#7C829A] hover:text-[#DC2626] cursor-pointer"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6" />
        <path d="M10 11v6M14 11v6" />
      </svg>
    </button>
  );
}

const TH = ({ children, className = "" }) => (
  <th
    className={`h-[44px] px-6 py-3 text-left align-middle bg-white border-b border-[#E5E8F3] text-[#5F6477] font-OpenSansSemiBold text-[12px] leading-[18px] ${className}`}
  >
    {children}
  </th>
);

const TD = ({ children, className = "" }) => (
  <td
    className={`px-6 py-4 border-b border-[#E5E8F3] text-[#1A2332] font-OpenSansRegular text-[14px] leading-5 align-middle ${className}`}
  >
    {children}
  </td>
);

/**
 * One day of logged hours for one technician, added or edited. Mounted only
 * while open, so every open starts from the row it was handed without an
 * effect to reset it.
 */
function HoursModal({ technicians, startDate, row, onCancel, onSave }) {
  const editing = !!row;
  const [techId, setTechId] = useState(row?.techId ?? technicians[0]?.id ?? "");
  const [date, setDate] = useState(
    startDate
      ? startDate
          .clone()
          .add(row?.dayDiff ?? 0, "days")
          .format("YYYY-MM-DD")
      : "",
  );
  const [regularTime, setRegularTime] = useState(row?.regularTime ?? "");
  const [overTime, setOverTime] = useState(row?.overTime ?? "");

  const submit = () => {
    const tech = technicians.find((entry) => String(entry.id) === String(techId));
    if (!tech) {
      toast("Pick a technician");
      return;
    }
    if (!date) {
      toast("Pick a date");
      return;
    }
    onSave({
      tech,
      // Rows are stored as an offset from the work order's start date, not as a
      // calendar date, so that is what the picked day is converted back into.
      dayDiff: startDate ? moment(date, "YYYY-MM-DD").diff(startDate.clone().startOf("day"), "days") : 0,
      // Parsed here as well as on blur, so saving straight from a field that
      // still reads "1" stores 01:00 rather than whatever was typed.
      regularTime: regularTime ? parseUserTime(regularTime) : "",
      overTime: overTime ? parseUserTime(overTime) : "",
    });
  };

  // Enter finishes the field, not the form: it tidies what was typed ("1" ->
  // "01:00") and leaves the modal open. Saving is the Save button's job.
  const commitOnEnter = (value, setValue) => (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    setValue(value ? parseUserTime(value) : "");
  };

  const fieldClass =
    "self-stretch h-10 px-3 rounded-md border border-[#E5E8F3] outline-none focus:border-[#0065FD] bg-white text-[#1A2332] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px]";
  const labelClass = "text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize";

  return (
    <div className="fixed inset-0 w-screen h-screen bg-black bg-opacity-30 flex items-center justify-center z-[60]">
      <OutsideClickHandler onOutsideClick={onCancel}>
        <div className="w-[440px] bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]">
          <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[#E5E8F3]">
            <div className="flex flex-col gap-1">
              <span className="text-[#1A2332] font-OpenSansSemiBold text-[20px] leading-7 capitalize">
                {editing ? "Edit Hours" : "Add Hours"}
              </span>
              <span className="text-[#7C829A] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px]">
                One technician, one day. Hours take 1, 1:30, 130 or 1.5.
              </span>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-[#1A2332] cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5 flex flex-col items-start gap-4">
            <div className="self-stretch flex flex-col items-start gap-1">
              <label className={labelClass}>Technician</label>
              <select
                value={techId}
                onChange={(e) => setTechId(e.target.value)}
                className={fieldClass + " cursor-pointer"}
              >
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.user?.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="self-stretch flex flex-col items-start gap-1">
              <label className={labelClass}>Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={fieldClass + " cursor-pointer"}
              />
            </div>

            <div className="self-stretch flex items-start gap-4">
              <div className="flex-1 flex flex-col items-start gap-1">
                <label className={labelClass}>Regular Hours</label>
                <TimeInput
                  value={regularTime}
                  onChange={setRegularTime}
                  onKeyDown={commitOnEnter(regularTime, setRegularTime)}
                  blankOnEmpty
                  placeholder="00:00"
                  className={fieldClass + " text-center placeholder:text-[#868DA6]"}
                />
              </div>
              <div className="flex-1 flex flex-col items-start gap-1">
                <label className={labelClass}>Overtime Hours</label>
                <TimeInput
                  value={overTime}
                  onChange={setOverTime}
                  onKeyDown={commitOnEnter(overTime, setOverTime)}
                  blankOnEmpty
                  placeholder="00:00"
                  className={fieldClass + " text-center placeholder:text-[#868DA6]"}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
            <button
              type="button"
              onClick={onCancel}
              className="h-10 px-1 rounded-md text-[#7C829A] font-OpenSansSemiBold text-[16px] leading-6 capitalize cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="h-10 px-4 rounded-lg bg-[#0065FD] flex items-center justify-center text-white font-OpenSansSemiBold text-[16px] leading-6 capitalize cursor-pointer"
            >
              {editing ? "Save" : "Add Row"}
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
}

/**
 * One supplied material, added or edited. Same shape as HoursModal: mounted
 * only while open, so it starts from the row it was handed.
 */
function MaterialModal({ row, onCancel, onSave }) {
  const editing = !!row;
  const [quantity, setQuantity] = useState(row?.quantity ?? "");
  const [description, setDescription] = useState(row?.description ?? "");

  const submit = () => {
    if (!description.trim()) {
      toast("Describe the material");
      return;
    }
    onSave({ quantity: quantity.trim(), description: description.trim() });
  };

  const fieldClass =
    "self-stretch h-10 px-3 rounded-md border border-[#E5E8F3] outline-none focus:border-[#0065FD] bg-white text-[#1A2332] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] placeholder:text-[#868DA6]";
  const labelClass = "text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize";

  return (
    <div className="fixed inset-0 w-screen h-screen bg-black bg-opacity-30 flex items-center justify-center z-[60]">
      <OutsideClickHandler onOutsideClick={onCancel}>
        <div className="w-[440px] bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]">
          <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[#E5E8F3]">
            <div className="flex flex-col gap-1">
              <span className="text-[#1A2332] font-OpenSansSemiBold text-[20px] leading-7 capitalize">
                {editing ? "Edit Material" : "Add Material"}
              </span>
              <span className="text-[#7C829A] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px]">
                Anything supplied that is not on a purchase order.
              </span>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-[#1A2332] cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5 flex flex-col items-start gap-4">
            <div className="self-stretch flex flex-col items-start gap-1">
              <label className={labelClass}>Quantity</label>
              <input
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                className={fieldClass}
              />
            </div>

            <div className="self-stretch flex flex-col items-start gap-1">
              <label className={labelClass}>Description</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Item description"
                className={fieldClass}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
            <button
              type="button"
              onClick={onCancel}
              className="h-10 px-1 rounded-md text-[#7C829A] font-OpenSansSemiBold text-[16px] leading-6 capitalize cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="h-10 px-4 rounded-lg bg-[#0065FD] flex items-center justify-center text-white font-OpenSansSemiBold text-[16px] leading-6 capitalize cursor-pointer"
            >
              {editing ? "Save" : "Add Row"}
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
}

/* ------------------------------------------------------------------- page */

export default function UpdatedServiceTicket() {
  const navigate = useNavigate();
  const { woId } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewShown, setPreviewShown] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendConfirmShown, setSendConfirmShown] = useState(false);
  // Saving, generating the PDF and handing it to the mailer takes a few
  // seconds; without this the buttons look like they did nothing.
  const [sending, setSending] = useState(false);
  const [reviewConfirmShown, setReviewConfirmShown] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  // null when closed; otherwise the row being edited, or "new" for a fresh one.
  const [hoursModalRow, setHoursModalRow] = useState(null);
  // The row a delete is waiting on confirmation for.
  const [hoursRowToDelete, setHoursRowToDelete] = useState(null);
  // Likewise for a refrigerant entry, which is deleted on the server directly.
  const [refrigerantToDelete, setRefrigerantToDelete] = useState(null);
  // null when closed; otherwise the material being edited, or "new".
  const [materialModalRow, setMaterialModalRow] = useState(null);
  const [materialToDelete, setMaterialToDelete] = useState(null);

  // Editable state
  // Typed by hand, comma separated, and stored on the work order exactly as
  // typed — wo.ticketReceipients is itself a comma-separated string.
  const [ccRecipients, setCcRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [servicesProvided, setServicesProvided] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [hourRows, setHourRows] = useState([]);
  const [miscMaterials, setMiscMaterials] = useState([]);
  // Purchase orders are not part of the work order payload, so they are read
  // on their own — see the load effect below.
  const [pos, setPos] = useState([]);

  // Refrigerants live in their own tables, so they save as you go rather than
  // with the draft.
  const [refrigerantCategories, setRefrigerantCategories] = useState([]);
  const [refrigerantTypes, setRefrigerantTypes] = useState({});
  const [woMaterials, setWoMaterials] = useState([]);
  const [addingFor, setAddingFor] = useState(null);
  const [newRefrigerant, setNewRefrigerant] = useState({
    typeId: "",
    quantity: "",
  });

  const currentUser = UserService.getCurrentUser();
  const canEdit = can("wo:updateServiceTicket");
  const canSend = can("wo:sendServiceTicket");

  /* ---------------------------------------------------------------- load */

  const hydrate = useCallback((wo) => {
    setCcRecipients(wo.ticketReceipients || "");
    setServicesProvided(wo.servicesProvided || "");
    setRecommendations(wo.recommendations || "");

    // One row per logged day per technician — the same entries the printed
    // ticket lists, keyed back to the technician they belong to.
    const rows = [];
    (wo.assignedTechs || []).forEach((tech) => {
      let entries = [];
      try {
        entries = tech.timesheet ? JSON.parse(tech.timesheet) : [];
      } catch {
        entries = [];
      }
      entries.forEach((entry, index) => {
        rows.push({
          key: `${tech.id}-${index}-${entry.dayDiff ?? 0}`,
          techId: tech.id,
          techName: tech.user?.name || "",
          avatar: tech.user?.avatar,
          dayDiff: entry.dayDiff ?? 0,
          regularTime: entry.regularTime || "",
          overTime: entry.overTime || "",
          travelTime: entry.travelTime || "",
        });
      });
    });
    setHourRows(rows);

    let materials = [];
    try {
      materials = wo.materials ? JSON.parse(wo.materials) : [];
    } catch {
      materials = [];
    }
    setMiscMaterials(materials.map((material, index) => ({ key: `m-${index}`, ...material })));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    WOService.getWorkOrderById(woId)
      .then((response) => {
        if (cancelled || response.status !== 200) return;
        setOrder(response.data);
        hydrate(response.data);
      })
      .catch((error) => handleError(error))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [woId, hydrate]);

  // /api/wo/getById does not join wo.pos, so an order arrives with no purchase
  // orders on it and Parts Supplied would always read as empty. They come from
  // the endpoint the work order's own PO tab uses, which carries poItems.
  useEffect(() => {
    if (!woId) return undefined;
    let cancelled = false;
    POService.getPurchaseOrdersByWoId(woId)
      .then((response) => {
        if (cancelled || response.status !== 200) return;
        setPos(response.data || []);
      })
      .catch((error) => handleError(error));
    return () => {
      cancelled = true;
    };
  }, [woId]);

  // Refrigerant panels: the tenant's categories, their types, and this work
  // order's rows.
  useEffect(() => {
    let cancelled = false;
    MaterialService.get()
      .then(async (response) => {
        const categories = (response.data || []).filter(
          (category) => isRefrigerantCategory(category.name) && category.isActive !== false,
        );
        if (cancelled) return;
        setRefrigerantCategories(categories);
        const typeEntries = await Promise.all(
          categories.map(async (category) => {
            try {
              const types = await MaterialService.getTypes(category.id);
              return [category.id, types.data || []];
            } catch {
              return [category.id, []];
            }
          }),
        );
        if (!cancelled) setRefrigerantTypes(Object.fromEntries(typeEntries));
      })
      .catch((error) => handleError(error));
    return () => {
      cancelled = true;
    };
  }, []);

  const loadWoMaterials = useCallback(() => {
    if (!woId) return;
    MaterialService.getWoMaterials(woId)
      .then((response) => setWoMaterials(response.data || []))
      .catch((error) => handleError(error));
  }, [woId]);

  useEffect(() => loadWoMaterials(), [loadWoMaterials]);

  /* -------------------------------------------------------------- derived */

  const startDate = order?.startDateString ? moment(order.startDateString, "MM/DD/YYYY") : null;

  const totals = useMemo(
    () =>
      hourRows.reduce(
        (acc, row) => {
          acc.regular += minutesOf(row.regularTime) + minutesOf(row.travelTime);
          acc.over += minutesOf(row.overTime);
          return acc;
        },
        { regular: 0, over: 0 },
      ),
    [hourRows],
  );

  // One row per purchase-order line item, falling back to the PO's own
  // description for a PO that carries no items — the same rule the printed
  // ticket applies, so the two cannot disagree.
  const parts = useMemo(() => {
    const rows = [];
    pos.forEach((po) => {
      const items = po.poItems || [];
      if (items.length) {
        items.forEach((item) =>
          rows.push({
            key: `po-${po.id}-${item.id}`,
            number: po.number,
            description: item.description || po.description || "",
          }),
        );
      } else {
        rows.push({
          key: `po-${po.id}`,
          number: po.number,
          description: po.description || "",
        });
      }
    });
    return rows;
  }, [pos]);

  const panelMaterials = (category) => woMaterials.filter((material) => material.materialCategory?.id === category.id);

  const companyCategory = refrigerantCategories.find((category) => !isCustomerSupplied(category.name));
  const customerCategory = refrigerantCategories.find((category) => isCustomerSupplied(category.name));

  /* --------------------------------------------------------------- saving */

  const buildDraft = () => {
    // Group the edited rows back into each technician's own timesheet, and keep
    // the aggregate in step so the ticket and the totals cannot disagree.
    const byTech = new Map();
    (order.assignedTechs || []).forEach((tech) => byTech.set(tech.id, []));
    hourRows.forEach((row) => {
      if (!byTech.has(row.techId)) byTech.set(row.techId, []);
      byTech.get(row.techId).push({
        dayDiff: row.dayDiff,
        regularTime: isBlankHours(row.regularTime) ? "00:00" : row.regularTime,
        overTime: isBlankHours(row.overTime) ? "00:00" : row.overTime,
        ...(row.travelTime ? { travelTime: row.travelTime } : {}),
      });
    });

    const timesheetFull = [...byTech.entries()].map(([techId, entries]) => {
      const regular = entries.reduce((sum, entry) => sum + minutesOf(entry.regularTime), 0);
      const over = entries.reduce((sum, entry) => sum + minutesOf(entry.overTime), 0);
      const travel = entries.reduce((sum, entry) => sum + minutesOf(entry.travelTime), 0);
      return {
        id: techId,
        timesheet: JSON.stringify(entries),
        totalTimesheet: JSON.stringify({
          regularTime: getFormattedHoursFromMins(regular),
          overTime: getFormattedHoursFromMins(over),
          travelTime: getFormattedHoursFromMins(travel),
        }),
      };
    });

    return {
      // Tidied on the way out so a trailing comma or stray spacing cannot
      // reach the mailer, which splits this field on commas.
      ticketReceipients: ccRecipients
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean)
        .join(","),
      servicesProvided,
      recommendations,
      timesheetFull: JSON.stringify(timesheetFull),
      materialsFull: JSON.stringify(
        miscMaterials.map(({ description, quantity, attachment }) => ({
          description: description || "",
          quantity: quantity || "",
          attachment: attachment ?? null,
        })),
      ),
      isServiceTicketEdited: true,
    };
  };

  const save = async ({ silent } = {}) => {
    if (!order) return false;
    setSaving(true);
    try {
      const response = await WOService.attachMaterials({
        id: order.id,
        data: buildDraft(),
        eventUser: currentUser,
      });
      if (response.status === 201 || response.status === 200) {
        setOrder(response.data);
        hydrate(response.data);
        if (!silent) toast("Service ticket saved");
        return true;
      }
      return false;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const previewTicket = async () => {
    if (!order) return;
    setPreviewLoading(true);
    try {
      await save({ silent: true });
      await WOService.previewServiceTicket(order.id);
      setPreviewShown(true);
    } catch (error) {
      handleError(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendToCustomer = async () => {
    if (sending) return;
    setSending(true);
    try {
      const saved = await save({ silent: true });
      if (!saved) return;
      const response = await WOService.sendServiceTicket(order.id);
      if (response.status === 200) {
        setOrder((prev) => ({ ...prev, ...response.data }));
        toast("Service ticket sent successfully");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setSending(false);
    }
  };

  const markAsReviewed = async () => {
    if (reviewing) return;
    setReviewing(true);
    try {
      const saved = await save({ silent: true });
      if (!saved) return;
      const response = await WOService.update({
        id: order.id,
        data: { status: 6 },
      });
      if (response.status === 201 || response.status === 200) {
        // The update endpoint answers with a slimmer work order than getById,
        // so only the status is taken from it.
        setOrder((prev) => ({ ...prev, status: response.data.status }));
        toast(vocabularize("Work order marked as reviewed"));
      }
    } catch (error) {
      handleError(error);
    } finally {
      setReviewing(false);
    }
  };

  /* -------------------------------------------------------- row mutations */

  const addHourRow = () => {
    if (!(order?.assignedTechs || []).length) {
      toast("Assign a technician to the work order first");
      return;
    }
    setHoursModalRow("new");
  };

  const editHourRow = (row) => {
    if (!canEdit) return;
    setHoursModalRow(row);
  };

  const removeHourRow = (row) => {
    setHourRows((rows) => rows.filter((entry) => entry.key !== row.key));
    setHoursRowToDelete(null);
  };

  const editMaterialRow = (row) => {
    if (!canEdit) return;
    setMaterialModalRow(row);
  };

  const commitMaterialRow = ({ quantity, description }) => {
    if (materialModalRow === "new") {
      setMiscMaterials((rows) => [...rows, { key: `m-new-${Date.now()}`, quantity, description }]);
    } else {
      setMiscMaterials((rows) =>
        rows.map((row) => (row.key === materialModalRow.key ? { ...row, quantity, description } : row)),
      );
    }
    setMaterialModalRow(null);
  };

  const removeMaterialRow = (row) => {
    setMiscMaterials((rows) => rows.filter((entry) => entry.key !== row.key));
    setMaterialToDelete(null);
  };

  const commitHourRow = ({ tech, dayDiff, regularTime, overTime }) => {
    const patch = {
      techId: tech.id,
      techName: tech.user?.name || "",
      avatar: tech.user?.avatar,
      dayDiff,
      regularTime,
      overTime,
    };
    if (hoursModalRow === "new") {
      setHourRows((rows) => [...rows, { key: `new-${Date.now()}`, travelTime: "", ...patch }]);
    } else {
      updateHourRow(hoursModalRow.key, patch);
    }
    setHoursModalRow(null);
  };

  const updateHourRow = (key, patch) =>
    setHourRows((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const addRefrigerant = async (category) => {
    if (!newRefrigerant.typeId || !newRefrigerant.quantity) {
      toast("Pick a type and enter a quantity");
      return;
    }
    try {
      await MaterialService.addWoMaterial({
        woId: Number(woId),
        materialCategoryId: category.id,
        materialTypeId: Number(newRefrigerant.typeId),
        quantity: Number(newRefrigerant.quantity),
        isAdded: true,
      });
      setAddingFor(null);
      setNewRefrigerant({ typeId: "", quantity: "" });
      loadWoMaterials();
    } catch (error) {
      handleError(error);
    }
  };

  const removeRefrigerant = async (material) => {
    setRefrigerantToDelete(null);
    try {
      await MaterialService.deleteWoMaterial(material.id);
      loadWoMaterials();
    } catch (error) {
      handleError(error);
    }
  };

  /* ---------------------------------------------------------------- render */

  if (loading || !order) {
    return <Loader variant="block" className="py-12" ariaLabel="loading-service-ticket" />;
  }

  const provider = order.serviceTicketProvider;
  const lastUpdated = order.updatedAt || order.stSentDate;

  return (
    <div className="w-full min-w-[1280px] h-full flex flex-col bg-white">
      {materialModalRow && (
        <MaterialModal
          row={materialModalRow === "new" ? null : materialModalRow}
          onCancel={() => setMaterialModalRow(null)}
          onSave={commitMaterialRow}
        />
      )}

      {hoursModalRow && (
        <HoursModal
          technicians={order.assignedTechs || []}
          startDate={startDate}
          row={hoursModalRow === "new" ? null : hoursModalRow}
          onCancel={() => setHoursModalRow(null)}
          onSave={commitHourRow}
        />
      )}

      <PreviewSTModal
        shown={previewShown}
        wo={order}
        pdfUrl={getServiceTicketUrl(order.number)}
        cancelClicked={() => setPreviewShown(false)}
      />
      <ConfirmDialog
        title="DELETE MATERIAL"
        description={
          materialToDelete
            ? `Are you sure you want to delete "${materialToDelete.description || "this material"}"?`
            : ""
        }
        shown={!!materialToDelete}
        onConfirmClicked={() => removeMaterialRow(materialToDelete)}
        onCancelClicked={() => setMaterialToDelete(null)}
      />
      <ConfirmDialog
        title="DELETE REFRIGERANT"
        description={
          refrigerantToDelete
            ? `Are you sure you want to delete ${refrigerantToDelete.materialType?.name || "this entry"} (${
                refrigerantToDelete.quantity
              }) from ${refrigerantToDelete.panel}?`
            : ""
        }
        shown={!!refrigerantToDelete}
        onConfirmClicked={() => removeRefrigerant(refrigerantToDelete)}
        onCancelClicked={() => setRefrigerantToDelete(null)}
      />
      <ConfirmDialog
        title="DELETE HOURS"
        description={
          hoursRowToDelete
            ? `Are you sure you want to delete ${hoursRowToDelete.techName || "this technician"}'s hours for ${
                startDate ? startDate.clone().add(hoursRowToDelete.dayDiff, "days").format("MMM D, YYYY") : "this day"
              }?`
            : ""
        }
        shown={!!hoursRowToDelete}
        onConfirmClicked={() => removeHourRow(hoursRowToDelete)}
        onCancelClicked={() => setHoursRowToDelete(null)}
      />
      <ConfirmDialog
        title="SEND SERVICE TICKET"
        description="Are you sure you want to send this service ticket to the customer?"
        shown={sendConfirmShown}
        onConfirmClicked={() => {
          setSendConfirmShown(false);
          sendToCustomer();
        }}
        onCancelClicked={() => setSendConfirmShown(false)}
      />
      <ConfirmDialog
        title="MARK AS REVIEWED"
        description={vocabularize("Are you sure you want to mark this work order as reviewed?")}
        shown={reviewConfirmShown}
        onConfirmClicked={() => {
          setReviewConfirmShown(false);
          markAsReviewed();
        }}
        onCancelClicked={() => setReviewConfirmShown(false)}
      />

      {/* Header. The page title itself lives in the app header
          (Layout/header.js), so this bar carries the back link, the actions and
          the work order's identity strip. */}
      <div className="shrink-0 w-full bg-white border-b border-[#E5E8F3] px-8 py-4 flex flex-col items-start gap-[15.5px]">
        <div className="self-stretch flex flex-col items-start gap-2">
          <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-2 cursor-pointer w-fit">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
              <path
                d="M6.02851 2.97978C6.22377 3.17504 6.22377 3.49155 6.02851 3.68681L2.21532 7.5L6.02851 11.3132C6.22377 11.5085 6.22377 11.825 6.02851 12.0202C5.83325 12.2155 5.51667 12.2155 5.32141 12.0202L1.15474 7.85359C0.959478 7.65833 0.959478 7.34175 1.15474 7.14649L5.32141 2.97978C5.51667 2.78452 5.83325 2.78452 6.02851 2.97978Z"
                fill="#868DA6"
              />
              <path
                d="M13.1667 7.00004C13.4428 7.00004 13.6667 7.2239 13.6667 7.50004C13.6667 7.77618 13.4428 8.00004 13.1667 8.00004H1.83334C1.5572 8.00004 1.33334 7.77618 1.33334 7.50004C1.33334 7.2239 1.5572 7.00004 1.83334 7.00004H13.1667Z"
                fill="#868DA6"
              />
            </svg>
            <span className="text-[#7C829A] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px]">
              {`Back to ${vocabularize("Work Order")} #${order.number}`}
            </span>
          </button>

          <div className="self-stretch flex items-center justify-end gap-4">
            <div className="flex items-center gap-3">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => save()}
                  disabled={saving}
                  className="h-[40px] px-4 rounded-lg border border-[#C9CEE2] bg-white flex items-center justify-center gap-2 cursor-pointer shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6 capitalize disabled:opacity-60"
                >
                  {saving ? (
                    <Loader size={16} ariaLabel="saving-service-ticket" />
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1A2332"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0"
                    >
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <path d="M17 21v-8H7v8M7 3v5h8" />
                    </svg>
                  )}
                  <span>{saving ? "Saving…" : "Save Changes"}</span>
                </button>
              )}
              {canSend && (
                <button
                  type="button"
                  onClick={() => setReviewConfirmShown(true)}
                  disabled={sending || reviewing || order.status >= 6}
                  className="h-[40px] px-4 rounded-lg border border-[#C9CEE2] bg-white flex items-center justify-center gap-2 cursor-pointer disabled:cursor-default disabled:opacity-60 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6 capitalize"
                >
                  {reviewing ? (
                    <Loader size={16} ariaLabel="marking-service-ticket-reviewed" />
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1A2332"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <path d="M22 4L12 14.01l-3-3" />
                    </svg>
                  )}
                  <span>{reviewing ? "Saving…" : order.status >= 6 ? "Reviewed" : "Mark as Reviewed"}</span>
                </button>
              )}
              <button
                type="button"
                onClick={previewTicket}
                disabled={previewLoading}
                className="h-[40px] px-4 rounded-lg border border-[#C9CEE2] bg-white flex items-center justify-center gap-2 cursor-pointer shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6 capitalize disabled:opacity-60"
              >
                {previewLoading ? (
                  <Loader size={16} ariaLabel="preparing-service-ticket" />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1A2332"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6M12 12v6M9 15l3 3 3-3" />
                  </svg>
                )}
                <span>{previewLoading ? "Preparing…" : "Preview/Download"}</span>
              </button>
              {canSend && (
                <button
                  type="button"
                  onClick={() => setSendConfirmShown(true)}
                  disabled={sending || reviewing}
                  className="h-[40px] px-4 rounded-lg bg-[#0065FD] flex items-center justify-center gap-2 cursor-pointer disabled:cursor-default disabled:opacity-70 text-white font-OpenSansSemiBold text-[16px] leading-6 capitalize"
                >
                  {sending ? (
                    <Loader variant="button" ariaLabel="sending-service-ticket" />
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0"
                    >
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                  )}
                  <span>{sending ? "Sending…" : "Send to Customer"}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <WoMetaRow wo={order} date={startDate ? startDate.format("MMM D, YYYY") : ""} />
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-10">
        <div className="w-[896px] max-w-[896px] mx-auto py-8 flex flex-col items-start gap-6">
          <Card title="Information Provided By">
            <div className="self-stretch p-4 flex items-start gap-8">
              <div className="flex-1 flex items-center gap-4">
                <img
                  src={getAvatarUrl(provider?.avatar)}
                  alt=""
                  className="w-[48px] h-[48px] rounded-full object-cover"
                />
                <div className="flex flex-col items-start">
                  <span className="text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6 capitalize">
                    {provider?.name || "—"}
                  </span>
                  <span className="text-[#7C829A] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px]">
                    {provider?.roles?.[0]?.name || "Technician"}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-right text-[#7C829A] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase">
                  Last Updated
                </span>
                <span className="text-right text-[#1A2332] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px]">
                  {lastUpdated ? formatDate(lastUpdated, "MMM D, YYYY h:mm A") : "—"}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Send To">
            <div className="self-stretch p-4 flex flex-col items-start gap-5">
              <div className="self-stretch flex flex-col items-start gap-2">
                <label className="text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize">
                  CC Recipients
                </label>
                <input
                  value={ccRecipients}
                  onChange={(e) => setCcRecipients(e.target.value)}
                  placeholder="recipient1@example.com,recipient2@example.com..."
                  className="self-stretch h-10 px-3 rounded-[6px] border border-[#E5E8F3] outline-none focus:border-[#0065FD] text-[#1A2332] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] placeholder:text-[#868DA6]"
                />
              </div>

              <div className="self-stretch flex flex-col items-start gap-1">
                <label className="text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize">
                  Message <span className="text-[#7C829A]">(optional)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a short note to include above the service ticket..."
                  className="self-stretch h-[96px] p-3 rounded-md bg-[#FBFCFF] border border-[#E5E8F3] outline-none focus:border-[#0065FD] resize-y text-[#1A2332] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] placeholder:text-[#868DA6]"
                />
              </div>
            </div>
          </Card>

          <Card title="Services Provided">
            <RichTextEditor
              value={servicesProvided}
              onChange={setServicesProvided}
              placeholder="Describe the work performed on this visit..."
            />
          </Card>

          <Card title={vocabularize("Recommendations")}>
            <RichTextEditor
              value={recommendations}
              onChange={setRecommendations}
              placeholder="Add recommendations for future maintenance..."
              minHeight="140px"
            />
          </Card>

          <Card title="Technician Hours" action={canEdit && <AddRowButton onClick={addHourRow} />}>
            <table className="w-full">
              <thead>
                <tr className="bg-white border-b border-[#E5E8F3]">
                  <TH>Technician</TH>
                  <TH className="w-[170px]">Date</TH>
                  <TH className="w-[140px]">Regular Hours</TH>
                  <TH className="w-[140px] text-center">Overtime Hours</TH>
                  <TH className="w-[100px]" />
                </tr>
              </thead>
              <tbody>
                {/* Rows read; the modal is the only place they are changed. */}
                {hourRows.map((row) => (
                  <tr
                    key={row.key}
                    onClick={() => editHourRow(row)}
                    className={`border-b border-[#EEF0F9] ${canEdit ? "cursor-pointer hover:bg-[#F8F9FE]" : ""}`}
                  >
                    <TD className="font-OpenSansSemiBold">{row.techName || "—"}</TD>
                    <TD className="text-[#5F6477]">
                      {startDate ? startDate.clone().add(row.dayDiff, "days").format("MMM D, YYYY") : "—"}
                    </TD>
                    <TD className="text-center">{isBlankHours(row.regularTime) ? NO_HOURS : row.regularTime}</TD>
                    <TD className="text-center">{isBlankHours(row.overTime) ? NO_HOURS : row.overTime}</TD>
                    <TD>
                      <div className="flex items-center gap-3">
                        <EditRowButton
                          label={`Edit hours for ${row.techName}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            editHourRow(row);
                          }}
                        />
                        <DeleteRowButton
                          label={`Remove hours for ${row.techName}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setHoursRowToDelete(row);
                          }}
                        />
                      </div>
                    </TD>
                  </tr>
                ))}
                {hourRows.length === 0 && (
                  <tr>
                    <TD className="text-[#7C829A] italic" colSpan={5}>
                      No hours logged yet.
                    </TD>
                  </tr>
                )}
                <tr className="bg-[#F8F9FE]">
                  <TD className="h-[52px] border-b-0 text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize">
                    Total
                  </TD>
                  <TD className="border-b-0" />
                  <TD className="h-[52px] border-b-0 text-center text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6">
                    {totals.regular ? getFormattedHoursFromMins(totals.regular) : NO_HOURS}
                  </TD>
                  <TD className="h-[52px] border-b-0 text-center text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6">
                    {totals.over ? getFormattedHoursFromMins(totals.over) : NO_HOURS}
                  </TD>
                  <TD className="border-b-0" />
                </tr>
              </tbody>
            </table>
          </Card>

          <Card title="Parts Supplied">
            <table className="w-full">
              <thead>
                <tr className="bg-white border-b border-[#E5E8F3]">
                  <TH className="w-[160px]">PO Number</TH>
                  <TH>Description</TH>
                </tr>
              </thead>
              <tbody>
                {parts.map((part) => (
                  <tr key={part.key} className="border-b border-[#EEF0F9]">
                    <TD className="text-[#5F6477]">{part.number}</TD>
                    <TD>{part.description || "—"}</TD>
                  </tr>
                ))}
                {parts.length === 0 && (
                  <tr>
                    <TD className="text-[#7C829A] italic" colSpan={2}>
                      No parts supplied on this work order.
                    </TD>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

          <div className="self-stretch flex items-start gap-6">
            {[companyCategory, customerCategory].map((category, index) => {
              const title = index === 0 ? "Company Refrigerants" : "Customer Refrigerants";
              if (!category) {
                return (
                  <div key={title} className="flex-1">
                    <Card title={title}>
                      <div className="px-6 py-8 text-center text-[#7C829A] font-OpenSansRegular text-[13px] italic">
                        No {title.toLowerCase()} category configured.
                      </div>
                    </Card>
                  </div>
                );
              }
              const rows = panelMaterials(category);
              return (
                <div key={category.id} className="flex-1 min-w-0">
                  <Card
                    title={title}
                    action={canEdit && <AddRowButton label="Add" onClick={() => setAddingFor(category.id)} />}
                  >
                    <div className="self-stretch p-4 flex flex-col items-start gap-5">
                      {rows.map((material) => (
                        <div
                          key={material.id}
                          className="w-full p-3 rounded-lg bg-[#F9FAFB] border border-[#F3F4F6] flex items-center gap-3"
                        >
                          <div className="flex-1 flex flex-col items-start gap-1">
                            <span className="text-[#7C829A] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase">
                              Type
                            </span>
                            <span className="self-stretch text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 capitalize">
                              {material.materialType?.name || "—"}
                            </span>
                          </div>
                          <div className="flex-1 flex flex-col items-start gap-1">
                            <span className="text-[#7C829A] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase">
                              {`Qty (${category.unit || "lbs"})`}
                            </span>
                            <span className="self-stretch text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5">
                              {material.quantity}
                            </span>
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              aria-label="Remove refrigerant"
                              onClick={() =>
                                setRefrigerantToDelete({
                                  ...material,
                                  panel: title,
                                })
                              }
                              className="text-[#868DA6] hover:text-[#DC2626] cursor-pointer"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}

                      {addingFor === category.id && (
                        <div className="w-full px-4 py-3 rounded-lg border border-[#C9CEE2] flex items-center gap-3">
                          <select
                            value={newRefrigerant.typeId}
                            onChange={(e) =>
                              setNewRefrigerant((state) => ({
                                ...state,
                                typeId: e.target.value,
                              }))
                            }
                            className="flex-1 h-[32px] px-2 rounded-md border border-[#C9CEE2] outline-none text-[14px] font-OpenSansRegular"
                          >
                            <option value="">Select type</option>
                            {(refrigerantTypes[category.id] || []).map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                          <input
                            value={newRefrigerant.quantity}
                            onChange={(e) =>
                              setNewRefrigerant((state) => ({
                                ...state,
                                quantity: e.target.value,
                              }))
                            }
                            placeholder={category.unit || "lbs"}
                            className="w-[90px] h-[32px] px-2 rounded-md border border-[#C9CEE2] outline-none text-[14px] font-OpenSansRegular"
                          />
                          <button
                            type="button"
                            onClick={() => addRefrigerant(category)}
                            className="h-[32px] px-3 rounded-md bg-[#0065FD] text-white font-OpenSansSemiBold text-[13px] cursor-pointer"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => setAddingFor(null)}
                            className="h-[32px] px-3 rounded-md border border-[#C9CEE2] text-[#5F6477] font-OpenSansSemiBold text-[13px] cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      {rows.length === 0 && addingFor !== category.id && (
                        <div className="w-full py-8 flex flex-col items-center gap-2 text-center">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                          </svg>
                          <span className="text-[#94A3B8] font-OpenSansRegular text-[13px] italic">
                            {`No ${title.toLowerCase()} were utilized during this service.`}
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>

          <Card
            title="Misc Materials Supplied"
            action={canEdit && <AddRowButton onClick={() => setMaterialModalRow("new")} />}
          >
            <table className="w-full">
              <thead>
                <tr className="bg-white border-b border-[#E5E8F3]">
                  <TH className="w-[160px]">Quantity</TH>
                  <TH>Description</TH>
                  <TH className="w-[100px]" />
                </tr>
              </thead>
              <tbody>
                {/* Rows read; the modal is the only place they are changed. */}
                {miscMaterials.map((material) => (
                  <tr
                    key={material.key}
                    onClick={() => editMaterialRow(material)}
                    className={`border-b border-[#EEF0F9] ${canEdit ? "cursor-pointer hover:bg-[#F8F9FE]" : ""}`}
                  >
                    <TD>{material.quantity || "—"}</TD>
                    <TD>{material.description || "—"}</TD>
                    <TD>
                      <div className="flex items-center gap-3">
                        <EditRowButton
                          label="Edit material"
                          onClick={(event) => {
                            event.stopPropagation();
                            editMaterialRow(material);
                          }}
                        />
                        <DeleteRowButton
                          label="Remove material"
                          onClick={(event) => {
                            event.stopPropagation();
                            setMaterialToDelete(material);
                          }}
                        />
                      </div>
                    </TD>
                  </tr>
                ))}
                {miscMaterials.length === 0 && (
                  <tr>
                    <TD className="text-[#7C829A] italic" colSpan={3}>
                      No misc materials supplied.
                    </TD>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
}
